import db, { getTableCount, updateSyncStatus } from "../db/database";
import { fetchFromBusinessCentralEspecial, getCurrentEnvironment } from "../utils/businessCentralUtils";
import { syncAperturasDeCaja } from "./aperturaDeCajaSyncService";
import { syncCajas } from "./cajaSyncService";
import { syncCampaigns } from "./campaignSyncService";
import { syncCierresDeCaja } from "./cierreDeCajaSyncService";
import { syncClientAddresses } from "./clientAddressSyncService";
import { syncClients } from "./clientSyncService";
import { syncColegios } from "./colegioSyncService";
import { syncEmpresa } from "./empresaSyncService";
import { getAllActiveCoupons } from "./couponService";
import { syncFormasDePago } from "./formaDePagoSyncService";
import { syncInvoices } from "./invoiceSyncService";
import { syncProductLines } from "./productLinesService";
import { syncProducts } from "./productSyncService";
import { getAllSalesPrices } from "./salesPriceService";
import { syncTPVs } from "./tpvSyncService";
import { syncTurnos } from "./turnoSyncService";
import { syncUsers } from "./userSyncService";

let syncInterval = null;
let onlineListener = null;

const defaultConfig = {
	enabled: true,
	interval: 30,
	intervalUnit: "minutos",
	onStartup: true,
	whenOnline: true,
	// Nuevo flag: controlar sincronización del contador de importaciones
	syncSourceCounter: false,
};

export const getSyncConfig = async () => {
	try {
		const storedConfig = localStorage.getItem("syncConfig");
		if (storedConfig) {
			// Mezclar con defaults para asegurar nuevas claves como syncSourceCounter
			const parsed = JSON.parse(storedConfig);
			return { ...defaultConfig, ...parsed };
		}
		return defaultConfig;
	} catch (error) {
		console.error("Error al obtener configuración de sincronización:", error);
		return defaultConfig;
	}
};

export const setSyncConfig = async (config) => {
	try {
		localStorage.setItem("syncConfig", JSON.stringify(config));
		setupSync(config);

		return true;
	} catch (error) {
		console.error("Error al guardar configuración de sincronización:", error);
		throw error;
	}
};

const getIntervalInMs = (interval, unit) => {
	const baseMs = interval * 1000;

	switch (unit) {
		case "segundos":
			return baseMs;
		case "minutos":
			return baseMs * 60;
		case "horas":
			return baseMs * 60 * 60;
		default:
			return baseMs * 60;
	}
};

const handleOnlineStatus = () => {
	syncAll();
};

export const setupSync = async (config = null) => {
	if (!config) {
		config = await getSyncConfig();
	}

	clearSync();

	if (config.enabled) {
		const intervalMs = getIntervalInMs(config.interval, config.intervalUnit);
		syncInterval = setInterval(syncAll, intervalMs);

		if (config.whenOnline) {
			window.removeEventListener("online", handleOnlineStatus);
			window.addEventListener("online", handleOnlineStatus);
			onlineListener = handleOnlineStatus;
		}

		if (config.onStartup) {
			syncAll();
		}
	}
};

export const clearSync = () => {
	if (syncInterval) {
		clearInterval(syncInterval);
		syncInterval = null;
	}

	if (onlineListener) {
		window.removeEventListener("online", onlineListener);
		onlineListener = null;
	}
};

export const isOnline = () => {
	return navigator.onLine;
};

export const markAsPending = async (table, id) => {
	try {
		await db[table].update(id, { pendingSync: 1, lastModified: new Date() });
	} catch (error) {
		console.error(
			`Error al marcar como pendiente el registro ${id} en ${table}:`,
			error
		);
	}
};

export const markAsDeleted = async (table, id) => {
	try {
		await db[table].update(id, {
			pendingSync: 1,
			deleted: true,
			lastModified: new Date(),
		});
	} catch (error) {
		console.error(
			`Error al marcar como eliminado el registro ${id} en ${table}:`,
			error
		);
	}
};

export const getPendingSync = async (table) => {
	try {
		// Filtrar por pendingSync = 1 O que No no sea null ni undefined
		return await db[table]
			.filter(item => item.pendingSync === 1 || item.No === null || item.No === undefined)
			.toArray();
	} catch (error) {
		console.error(`Error al obtener registros pendientes de ${table}:`, error);
		return [];
	}
};

export const markAsSynced = async (table, id) => {
	try {
		await db[table].update(id, { pendingSync: 0, lastSynced: new Date() });
	} catch (error) {
		console.error(
			`Error al marcar como sincronizado el registro ${id} en ${table}:`,
			error
		);
	}
};

const logSyncTime = (operation, startTime) => {
	const endTime = Date.now();
	const timeInSeconds = ((endTime - startTime) / 1000).toFixed(2);
	console.log(`${operation} completado en ${timeInSeconds} segundos`);
};

// Función para emitir eventos de sincronización
const emitSyncEvent = (message, isComplete = false) => {
	const event = new CustomEvent('syncStatus', {
		detail: { message, isComplete }
	});
	window.dispatchEvent(event);
};

// Función para mostrar información del entorno y esperar decisión del usuario
const showEnvironmentInfo = async () => {
	try {
		const env = await getCurrentEnvironment();

		return new Promise((resolve, reject) => {
			let settled = false;
			const timeoutId = setTimeout(() => {
				if (!settled) {
					settled = true;
					console.log('Tiempo agotado de confirmación de entorno. Continuando automáticamente.');
					resolve();
				}
			}, 16000); // 15s del indicador + margen

			const event = new CustomEvent('environmentInfo', {
				detail: {
					company: env.company,
					env: env.env,
					baseUrl: env.bcBaseUrl,
					apiUrl: env.apiUrl,
					onContinue: () => {
						if (!settled) {
							settled = true;
							clearTimeout(timeoutId);
							console.log('Usuario decidió continuar con la sincronización');
							resolve();
						}
					},
					onCancel: () => {
						if (!settled) {
							settled = true;
							clearTimeout(timeoutId);
							console.log('Usuario canceló la sincronización');
							reject(new Error('Sincronización cancelada por el usuario'));
						}
					}
				}
			});
			window.dispatchEvent(event);
		});
	} catch (error) {
		console.error('Error al mostrar información del entorno:', error);
		throw error;
	}
};

// Función para manejar reintentos con visualización
const executeWithRetry = async (syncFunction, operationName, entityKey = null, maxRetries = 5) => {
	let lastError = null;

	for (let attempt = 1; attempt <= maxRetries; attempt++) {
		try {
			if (attempt === 1) {
				emitSyncEvent(`Sincronizando ${operationName}...`);
			} else {
				emitSyncEvent(`Reintentando ${operationName}... (${attempt}/${maxRetries})`);
			}

			const result = await syncFunction();

			// Si la función devuelve false, tratarlo como un error
			if (result === false) {
				throw new Error(`La sincronización de ${operationName} falló`);
			}

			// Registrar éxito en la base de datos si se proporciona entityKey
			if (entityKey) {
				try {
					const recordCount = await getTableCount(entityKey);
					await updateSyncStatus(entityKey, true, recordCount);
				} catch (error) {
					console.warn(`Error al actualizar estado de sincronización para ${entityKey}:`, error);
				}
			}

			return; // Éxito, salir del bucle
		} catch (error) {
			lastError = error;
			console.error(`Error en ${operationName} (intento ${attempt}/${maxRetries}):`, error);

			if (attempt < maxRetries) {
				emitSyncEvent(`Error en ${operationName} - Reintentando en 15 segundos... (${attempt}/${maxRetries})`);
				await new Promise(resolve => setTimeout(resolve, 15000)); // Esperar 15 segundos
			}
		}
	}

	// Si llegamos aquí, todos los intentos fallaron
	emitSyncEvent(`Error en ${operationName} después de ${maxRetries} intentos`);

	// Registrar error en la base de datos si se proporciona entityKey
	if (entityKey) {
		try {
			await updateSyncStatus(entityKey, false, 0, lastError?.message || 'Error desconocido');
		} catch (error) {
			console.warn(`Error al actualizar estado de fallo para ${entityKey}:`, error);
		}
	}

	// Emitir evento especial para mostrar modal de reinicio
	const criticalErrorEvent = new CustomEvent('criticalSyncError', {
		detail: {
			operationName,
			maxRetries,
			error: lastError
		}
	});
	window.dispatchEvent(criticalErrorEvent);

	throw lastError;
};

export const syncAll = async () => {
	try {
		if (!isOnline()) {
			return { success: false, message: "Sin conexión a internet" };
		}

		console.log("Iniciando proceso de sincronización completa...");

		// Mostrar información del entorno y esperar decisión del usuario
		try {
			await showEnvironmentInfo();
		} catch (error) {
			// Si el usuario canceló, detener el proceso
			if (error.message === 'Sincronización cancelada por el usuario') {
				console.log("Sincronización cancelada por el usuario");
				emitSyncEvent("Sincronización cancelada", true);
				return { success: false, message: "Sincronización cancelada por el usuario" };
			}
			throw error; // Re-lanzar otros errores
		}

		console.log("Ha llegado hasta aqui");

		emitSyncEvent("Iniciando sincronización...");
		const totalStartTime = Date.now();
		let startTime = Date.now();
		const config = await getSyncConfig();

		// Sincronizar Source_Counter (opcional por configuración)
		if (config.syncSourceCounter) {
			console.log("Sincronizando Contador de Importaciones...");
			startTime = Date.now();
			await executeWithRetry(
				() => fetchFromBusinessCentralEspecial("Importaciones_ActualizarSourceCounter"),
				"Contador de Importaciones"
			);
			logSyncTime("Sincronización de Contador de Importaciones", startTime);
		} else {
			console.log("Omitiendo sincronización de Contador de Importaciones por configuración");
		}

		// Sincronizar información de empresa
		console.log("Sincronizando información de empresa...");
		startTime = Date.now();
		await executeWithRetry(
			() => syncEmpresa(),
			"empresa",
			"empresa"
		);
		logSyncTime("Sincronización de información de empresa", startTime);

		// Sincronizar usuarios primero
		console.log("Sincronizando usuarios...");
		startTime = Date.now();
		await executeWithRetry(
			() => syncUsers(),
			"usuarios",
			"users"
		);
		logSyncTime("Sincronización de usuarios", startTime);

		// Sincronizar TPVs
		console.log("Sincronizando TPVs...");
		startTime = Date.now();
		await executeWithRetry(
			() => syncTPVs(),
			"tiendas",
			"tpvs"
		);
		logSyncTime("Sincronización de TPVs", startTime);

		// Luego sincronizar clientes
		console.log("Sincronizando clientes...");
		startTime = Date.now();
		await executeWithRetry(
			() => syncClients(),
			"clientes",
			"clients"
		);
		logSyncTime("Sincronización de clientes", startTime);

		// Sincronizar direcciones de clientes
		console.log("Sincronizando direcciones de clientes...");
		startTime = Date.now();
		await executeWithRetry(
			() => syncClientAddresses(),
			"direcciones",
			"clientAddresses"
		);
		logSyncTime("Sincronización de direcciones de clientes", startTime);

		// Sincronizar líneas de producto
		console.log("Sincronizando líneas de producto...");
		startTime = Date.now();
		await executeWithRetry(
			() => syncProductLines(),
			"líneas de producto",
			"productLines"
		);
		logSyncTime("Sincronización de líneas de producto", startTime);

		// Sincronizar facturas
		console.log("Sincronizando facturas...");
		startTime = Date.now();
		await executeWithRetry(
			() => syncInvoices(),
			"facturas",
			"invoices"
		);
		logSyncTime("Sincronización de facturas", startTime);

		// Sincronizar turnos
		console.log("Sincronizando turnos...");
		startTime = Date.now();
		await executeWithRetry(
			() => syncTurnos(),
			"turnos",
			"turnos"
		);
		logSyncTime("Sincronización de turnos", startTime);

		// Sincronizar aperturas de caja
		console.log("Sincronizando aperturas de caja...");
		startTime = Date.now();
		await executeWithRetry(
			() => syncAperturasDeCaja(),
			"aperturas de caja",
			"aperturaDeCaja"
		);
		logSyncTime("Sincronización de aperturas de caja", startTime);

		// Sincronizar cajas
		console.log("Sincronizando cajas...");
		startTime = Date.now();
		await executeWithRetry(
			() => syncCajas(),
			"cajas",
			"cajas"
		);
		logSyncTime("Sincronización de cajas", startTime);

		// Sincronizar cierres de caja
		console.log("Sincronizando cierres de caja...");
		startTime = Date.now();
		await executeWithRetry(
			() => syncCierresDeCaja(),
			"cierres de caja",
			"cierreDeCaja"
		);
		logSyncTime("Sincronización de cierres de caja", startTime);

		// Sincronizar formas de pago
		console.log("Sincronizando formas de pago...");
		startTime = Date.now();
		await executeWithRetry(
			() => syncFormasDePago(),
			"formas de pago",
			"formasPago"
		);
		logSyncTime("Sincronización de formas de pago", startTime);

		// Sincronizar colegios
		console.log("Sincronizando colegios...");
		startTime = Date.now();
		await executeWithRetry(
			() => syncColegios(),
			"colegios",
			"colegios"
		);
		logSyncTime("Sincronización de colegios", startTime);

		// Sincronizar cupones
		console.log("Sincronizando cupones...");
		startTime = Date.now();
		await executeWithRetry(
			() => getAllActiveCoupons(),
			"cupones",
			"coupons"
		);
		logSyncTime("Sincronización de cupones", startTime);

		// Sincronizar precios
		console.log("Sincronizando precios...");
		startTime = Date.now();
		await executeWithRetry(
			() => getAllSalesPrices(),
			"precios",
			"salesPrices"
		);
		logSyncTime("Sincronización de precios", startTime);

		// Después sincronizar productos
		console.log("Sincronizando productos...");
		startTime = Date.now();
		await executeWithRetry(
			() => syncProducts(),
			"productos",
			"products"
		);
		logSyncTime("Sincronización de productos", startTime);

		// Sincronizar campañas
		console.log("Sincronizando campañas...");
		startTime = Date.now();
		await executeWithRetry(
			() => syncCampaigns(),
			"campañas",
			"campaigns"
		);
		logSyncTime("Sincronización de campañas", startTime);

		const totalTime = ((Date.now() - totalStartTime) / 1000).toFixed(2);
		console.log(
			`Proceso de sincronización completado en ${totalTime} segundos`
		);
		emitSyncEvent("Sincronización completada", true);
		return { success: true, message: "Sincronización completada" };
	} catch (error) {
		console.error("Error durante la sincronización:", error);
		emitSyncEvent("Error en sincronización", true);
		return {
			success: false,
			message: "Error durante la sincronización: " + error.message,
		};
	}
};

export const markForSync = markAsPending;
export const markForDelete = markAsDeleted;

// Funciones para sincronizar modelos individuales
export const syncSingleModel = async (modelKey) => {
	try {
		if (!isOnline()) {
			throw new Error("Sin conexión a internet");
		}

		console.log(`Iniciando sincronización individual de ${modelKey}...`);

		// Mostrar información del entorno y esperar decisión del usuario
		try {
			await showEnvironmentInfo();
		} catch (error) {
			// Si el usuario canceló, detener el proceso
			if (error.message === 'Sincronización cancelada por el usuario') {
				console.log("Sincronización cancelada por el usuario");
				throw new Error("Sincronización cancelada por el usuario");
			}
			throw error; // Re-lanzar otros errores
		}

		const modelMappings = {
			'users': { func: syncUsers, name: 'usuarios' },
			'clients': { func: syncClients, name: 'clientes' },
			'products': { func: syncProducts, name: 'productos' },
			'productLines': { func: syncProductLines, name: 'líneas de producto' },
			'invoices': { func: syncInvoices, name: 'facturas' },
			'turnos': { func: syncTurnos, name: 'turnos' },
			'cajas': { func: syncCajas, name: 'cajas' },
			'aperturaDeCaja': { func: syncAperturasDeCaja, name: 'aperturas de caja' },
			'cierreDeCaja': { func: syncCierresDeCaja, name: 'cierres de caja' },
			'formasPago': { func: syncFormasDePago, name: 'formas de pago' },
			'colegios': { func: syncColegios, name: 'colegios' },
			'tpvs': { func: syncTPVs, name: 'tiendas' },
			'coupons': { func: getAllActiveCoupons, name: 'cupones' },
			'campaigns': { func: syncCampaigns, name: 'campañas' },
			'salesPrices': { func: getAllSalesPrices, name: 'precios' },
			'empresa': { func: syncEmpresa, name: 'empresa' }
		};

		const model = modelMappings[modelKey];
		if (!model) {
			throw new Error(`Modelo ${modelKey} no encontrado`);
		}

		await executeWithRetry(
			model.func,
			model.name,
			modelKey,
			3 // Solo 3 intentos para sincronización manual
		);

		return { success: true, message: `${model.name} sincronizado correctamente` };
	} catch (error) {
		console.error(`Error al sincronizar ${modelKey}:`, error);
		throw error;
	}
};
